import { ReactElement, useEffect, useRef, useState } from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';
import { NavLinkEndUser } from '@components/endUserDetails/components/navlinkEndUser/navlinkEndUser';
import { useEndUserStore } from '@store/enduser';
import { addNewSiteActiveNavData, addNewSiteUpcomingNavData, ISiteDataContextType } from '.';
import { useAddNewSiteStore } from '@store/lms/addNewSiteStore';
import { SiteStageLabel } from '@components/lms/addNewSite/components/siteInformation';
import { useSiteDetailsQuery } from '@services/hooks/lms/addNewSite';
import { Loader } from '@components/common';
import { useSiteAllDetailsStore } from '@store/siteAllDetailsStore';

export const AddNewSite = (): ReactElement => {
	const { setGliderRef, formId, isEditing, activeTabIndex, setActiveTabIndex, isConvertingUpcomingToActive } =
		useEndUserStore();
	const { siteInfo } = useAddNewSiteStore((state) => state);
	const { clearSiteDetails } = useSiteAllDetailsStore();

	const { data: siteData, isLoading } = useSiteDetailsQuery(formId ? String(formId) : null);

	const [tabNavData, setTabNavData] = useState(addNewSiteActiveNavData);
	const gliderRef = useRef<HTMLLIElement>(null);
	const listRef = useRef<HTMLUListElement | null>(null);
	// Clear site details store when component mounts for new sites
	useEffect(() => {
		if (!isEditing && !formId) {
			clearSiteDetails();
		}
	}, [isEditing, formId, clearSiteDetails]);

	// Clear site details store when component unmounts
	useEffect(() => {
		return (): void => {
			clearSiteDetails();
		};
	}, [clearSiteDetails]);

	useEffect(() => {
		setGliderRef(gliderRef);
	}, [gliderRef, setGliderRef]);

	// Immediate check for glue details navigation on mount
	useEffect(() => {
		const currentPath = window.location.pathname;
		const isNavigatingToGlueDetails = currentPath.includes('/glue-details');

		if (isNavigatingToGlueDetails && isEditing) {
			setTabNavData(addNewSiteActiveNavData);
			setActiveTabIndex(2); // Glue details is at index 2
		}
	}, []); // Empty dependency array for immediate execution

	// Handle initial navigation to glue details
	useEffect(() => {
		const currentPath = window.location.pathname;
		const isNavigatingToGlueDetails = currentPath.includes('/glue-details');

		if (isNavigatingToGlueDetails && isEditing) {
			setTabNavData(addNewSiteActiveNavData);
			setActiveTabIndex(2); // Glue details is at index 2
		}
	}, [isEditing]);

	useEffect(() => {
		if (siteInfo.siteStage === SiteStageLabel.Upcoming) {
			setTabNavData(addNewSiteUpcomingNavData);
		} else {
			setTabNavData(addNewSiteActiveNavData);
		}
	}, [siteInfo]);

	useEffect(() => {
		if ((isEditing || isConvertingUpcomingToActive) && siteData) {
			const isUpcoming = siteData.site_current_status_c === SiteStageLabel.Upcoming;

			// Conversion flow: show all 3 tabs (Active flow) and start from Site Information
			if (isUpcoming && isConvertingUpcomingToActive) {
				setTabNavData(addNewSiteActiveNavData);
				setActiveTabIndex(2);
				return;
			}

			// Check if we're navigating directly to glue details
			const currentPath = window.location.pathname;
			const isNavigatingToGlueDetails = currentPath.includes('/glue-details');

			if (isNavigatingToGlueDetails) {
				// Show all tabs including glue details and set active tab to glue details
				setTabNavData(addNewSiteActiveNavData);
				setActiveTabIndex(2); // Glue details is at index 2
				return;
			}

			// Default edit flow: hide Glue Details and move ahead by one tab
			const filteredTabs = isUpcoming ? addNewSiteUpcomingNavData : addNewSiteActiveNavData;

			setTabNavData(filteredTabs);
			setActiveTabIndex(activeTabIndex + 2);
		} else {
			setActiveTabIndex(0);
		}

		// eslint-disable-next-line
	}, [siteData, isEditing, isConvertingUpcomingToActive]);

	if (isLoading) {
		return <Loader />;
	}

	return (
		<section className="add-new-site-section">
			<div className="container">
				<div className="slide-tab-wrapper">
					<ul className="tabs" ref={listRef}>
						{tabNavData.map((navData) => (
							<NavLinkEndUser
								key={navData.id}
								listRef={listRef}
								value={navData.id}
								navData={navData}
								isTabIndexLogic={true}
							/>
						))}
						<li ref={gliderRef} className="glider"></li>
					</ul>
				</div>
				<Outlet context={{ siteData, isLoading, isEditing }} />
			</div>
		</section>
	);
};

export const useSiteContext = (): ISiteDataContextType => {
	return useOutletContext<ISiteDataContextType>();
};
